import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Home, User, RefreshCw, Search, Pencil, X, Save, Loader2, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import { STATUS_SANTRI_OPTIONS, STATUS_PENGABDIAN_OPTIONS } from "@/lib/santriSchema";
import { formatIsoDate } from "@/lib/perizinan";
import SantriPreviewModal from "@/components/SantriPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Columns shown in pembimbing's santri table (Username & Password hidden)
const COLS = [
  { key: "no", label: "No" },
  { key: "nama_santri", label: "Nama Santri" },
  { key: "jenis_kelamin", label: "JK" },
  { key: "daerah_asal", label: "Daerah Asal" },
  { key: "tahun_masuk_grq", label: "Angkatan" },
  { key: "status_santri", label: "Status Santri" },
  { key: "status_pengabdian", label: "Status Pengabdian" },
  { key: "lama_pengabdian", label: "Lama Pengabdian" },
  { key: "jumlah_hafalan_terakhir", label: "Jml. Hafalan Terakhir" },
  { key: "no_kontak_santri", label: "Kontak Santri" },
];

// Fields the pembimbing can edit
const EDITABLE = [
  { key: "status_santri", label: "Status Santri", type: "select", options: STATUS_SANTRI_OPTIONS },
  { key: "status_pengabdian", label: "Status Pengabdian", type: "select", options: STATUS_PENGABDIAN_OPTIONS },
  { key: "lama_pengabdian", label: "Lama Pengabdian", type: "text" },
  { key: "jumlah_hafalan_terakhir", label: "Jumlah Hafalan Terakhir (Update)", type: "text" },
];

export default function PembimbingDashboard() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(user?.profile || null);
  const [santri, setSantri] = useState([]);
  const [perizinan, setPerizinan] = useState([]);
  const [pQuery, setPQuery] = useState("");
  const [pDateFrom, setPDateFrom] = useState("");
  const [pDateTo, setPDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const loadProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: authHeader() });
      setProfile(res.data.profile || null);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/login", { replace: true }); }
    }
  };

  const loadSantri = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pembimbing/my-santri`, { headers: authHeader() });
      setSantri(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/login", { replace: true }); }
      else toast.error("Gagal memuat data santri bimbingan");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    loadProfile();
    loadSantri();
    loadPerizinan();
    /* eslint-disable-next-line */
  }, []);

  const loadPerizinan = async () => {
    try {
      const res = await axios.get(`${API}/perizinan/my-santri`, { headers: authHeader() });
      setPerizinan(res.data || []);
    } catch {}
  };

  const filteredPerizinan = useMemo(() => {
    const s = pQuery.trim().toLowerCase();
    return perizinan.filter((r) => {
      if (pDateFrom && r.tanggal_pengajuan < pDateFrom) return false;
      if (pDateTo && r.tanggal_pengajuan > pDateTo) return false;
      if (!s) return true;
      return (r.nama_lengkap || "").toLowerCase().includes(s);
    });
  }, [perizinan, pQuery, pDateFrom, pDateTo]);

  const doLogout = () => { logout(); navigate("/", { replace: true }); };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return santri;
    return santri.filter((r) => Object.values(r).some((v) => String(v || "").toLowerCase().includes(s)));
  }, [santri, q]);

  const openEdit = (r) => setModal({ data: { ...r } });
  const setField = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const saveEdit = async () => {
    if (!modal?.data) return;
    setSaving(true);
    try {
      const patch = {};
      EDITABLE.forEach((f) => { patch[f.key] = modal.data[f.key] ?? ""; });
      const res = await axios.patch(
        `${API}/pembimbing/santri/${modal.data.id}`,
        patch,
        { headers: authHeader() },
      );
      toast.success(`Data ${res.data.nama_santri || "santri"} diperbarui`);
      setModal(null);
      await loadSantri();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan";
      toast.error(typeof msg === "string" ? msg : "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <main className="min-h-screen bg-paper text-forest">
      <DashboardNav user={user} onLogout={doLogout} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs tracking-[0.4em] uppercase text-gold">Portal Pembimbing</span>
          <h1 className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-tight">
            Ahlan wa sahlan,<br />
            <span className="italic text-gold">{profile?.nama_lengkap || user?.name || "Ustadz/Ustadzah"}.</span>
          </h1>
          <p className="mt-4 font-arabic text-2xl md:text-3xl text-gold">جَزَاكَ اللَّهُ خَيْرًا</p>
        </motion.div>

        {/* Profile (read-only) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 p-6 md:p-8 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur"
          data-testid="pembimbing-profile"
        >
          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 rounded-full bg-forest text-parchment flex items-center justify-center flex-shrink-0">
              <User size={34} />
            </div>
            <div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Username</span>
              <p className="font-serif-display text-2xl mt-1">{user?.username}</p>
              <p className="mt-1 text-sm text-moss">Status: {profile?.status_pembimbing || "—"}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Field label="Nama Lengkap Pembimbing" value={profile?.nama_lengkap} />
            <Field label="Tanggal Lahir" value={profile?.tanggal_lahir} />
            <Field label="Alamat Lengkap" value={profile?.alamat} wide />
            <Field label="Tanggal Bergabung dengan GRQ" value={profile?.tanggal_bergabung} />
            <Field label="Status Pembimbing" value={profile?.status_pembimbing} />
          </div>
        </motion.div>

        {/* Santri table */}
        <div className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">Santri Bimbingan</span>
            <h2 className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight">
              Daftar santri di bawah bimbingan Anda
            </h2>
            <p className="mt-2 text-sm text-moss">
              Klik ikon edit untuk memperbarui <b>Status Santri</b>, <b>Status Pengabdian</b>,
              <b> Lama Pengabdian</b>, dan <b>Jumlah Hafalan Terakhir</b>.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / status"
                className="pl-9 pr-4 py-2.5 bg-white/70 border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[240px]"
                data-testid="pembdash-search" />
            </div>
            <button onClick={loadSantri} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors" data-testid="pembdash-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-forest text-parchment text-left">
                <tr>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Aksi</th>
                  {COLS.map((c) => <th key={c.key} className="px-3 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={COLS.length + 1} className="text-center py-10 text-moss">Memuat data…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={COLS.length + 1} className="text-center py-10 text-moss">
                    Belum ada santri di bawah bimbingan Anda. Hubungi Admin untuk penugasan.
                  </td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={r.id} className="border-t border-gold/15 hover:bg-sand/60 transition-colors" data-testid={`my-santri-row-${r.id}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setPreviewRow(r)} className="p-2 rounded-full hover:bg-forest hover:text-parchment transition-colors" aria-label="Preview data lengkap" title="Preview data lengkap" data-testid={`preview-my-santri-${r.id}`}>
                            <FileSearch size={14} />
                          </button>
                          <button onClick={() => openEdit(r)} className="p-2 rounded-full hover:bg-gold hover:text-forest transition-colors" aria-label="Edit" data-testid={`edit-my-santri-${r.id}`}>
                            <Pencil size={14} />
                          </button>
                        </div>
                      </td>
                      {COLS.map((c) => (
                        <td key={c.key} className={`px-3 py-2.5 ${c.key === "no" ? "text-moss" : ""} whitespace-nowrap`}>
                          {c.key === "no" ? i + 1 : r[c.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-3 text-xs text-moss">Total: <b>{filtered.length}</b> santri bimbingan</p>

        {/* Perizinan segment (read-only) */}
        <div className="mt-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="text-xs tracking-[0.4em] uppercase text-gold">Perizinan</span>
              <h2 className="mt-2 font-serif-display text-3xl md:text-4xl leading-tight">
                Pengajuan izin santri bimbingan
              </h2>
              <p className="mt-2 text-sm text-moss">Read-only. Approve/edit data dilakukan oleh Admin.</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-end gap-3 p-4 rounded-2xl border border-gold/30 bg-white/50">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Cari Nama</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
                <input value={pQuery} onChange={(e) => setPQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white/70 border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[220px]"
                  data-testid="pemb-perizinan-search" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Dari Tanggal</label>
              <input type="date" value={pDateFrom} onChange={(e) => setPDateFrom(e.target.value)}
                className="px-3 py-2 bg-white border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
                data-testid="pemb-perizinan-from" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Sampai</label>
              <input type="date" value={pDateTo} onChange={(e) => setPDateTo(e.target.value)}
                className="px-3 py-2 bg-white border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
                data-testid="pemb-perizinan-to" />
            </div>
            {(pQuery || pDateFrom || pDateTo) && (
              <button onClick={() => { setPQuery(""); setPDateFrom(""); setPDateTo(""); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-moss hover:text-gold">
                <X size={12} /> Bersihkan
              </button>
            )}
          </div>

          <div className="mt-6 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-forest text-parchment text-left">
                  <tr>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">No</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Tgl Pengajuan</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Nama Lengkap</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Alasan</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Rencana Kembali</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Approver</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Tgl Approve</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPerizinan.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-10 text-moss">Belum ada pengajuan izin dari santri bimbingan Anda.</td></tr>
                  ) : (
                    filteredPerizinan.map((r, i) => (
                      <tr key={r.id} className="border-t border-gold/15 hover:bg-sand/60 transition-colors">
                        <td className="px-3 py-2.5 text-moss">{i + 1}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{formatIsoDate(r.tanggal_pengajuan)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap font-medium">{r.nama_lengkap || "—"}</td>
                        <td className="px-3 py-2.5 max-w-[320px]">{r.alasan || "—"}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{formatIsoDate(r.tanggal_rencana_kembali)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{r.approver || "—"}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{r.tanggal_approve ? formatIsoDate(r.tanggal_approve) : "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {modal && (
          <EditModal
            record={modal.data}
            onChange={setField}
            onClose={() => setModal(null)}
            onSave={saveEdit}
            saving={saving}
          />
        )}
        {previewRow && (
          <SantriPreviewModal
            record={previewRow}
            onClose={() => setPreviewRow(null)}
            hideKeys={["pendapatan_ayah", "pendapatan_ibu", "pendapatan_wali"]}
            includeCredentials={false}
          />
        )}
      </AnimatePresence>
    </main>
  );
}

function Field({ label, value, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-moss">{label}</p>
      <p className="mt-1 text-forest text-sm md:text-base whitespace-pre-wrap">{value && String(value).trim() ? value : "—"}</p>
    </div>
  );
}

function EditModal({ record, onChange, onClose, onSave, saving }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}
      data-testid="pemb-edit-santri-modal">
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-xl overflow-hidden border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Update Progres Santri</span>
            <h3 className="font-serif-display text-forest text-2xl">{record.nama_santri || "Santri"}</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup"><X size={18} /></button>
        </div>

        <div className="px-6 py-6 space-y-4">
          {EDITABLE.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">{f.label}</label>
              {f.type === "select" ? (
                <select value={record[f.key] || ""} onChange={(e) => onChange(f.key, e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                  data-testid={`pemb-edit-${f.key}`}>
                  <option value="">— Pilih —</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type="text" value={record[f.key] ?? ""} onChange={(e) => onChange(f.key, e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                  data-testid={`pemb-edit-${f.key}`} autoComplete="off" />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gold/20 bg-parchment/95">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="pemb-edit-cancel">Batal</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors disabled:opacity-60" data-testid="pemb-edit-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function DashboardNav({ user, onLogout }) {
  return (
    <header className="fixed top-0 inset-x-0 z-40 bg-parchment/85 backdrop-blur-md border-b border-gold/30">
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="dash-brand">
          <img src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png" alt="Logo GRQ" className="h-11 w-11 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-moss">Portal Pembimbing</div>
            <div className="font-serif-display text-forest text-base">Generasi Rabbani Qurani</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs text-moss">
            <span className="tracking-[0.2em] uppercase">Ahlan</span>{" "}
            <span className="text-forest font-medium">{user?.name}</span>
          </span>
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold hover:text-forest transition-colors" data-testid="dash-home">
            <Home size={14} /> Beranda
          </Link>
          <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors" data-testid="dash-logout">
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </nav>
    </header>
  );
}
